import React, { useState, useEffect } from 'react';
import { Plus, ShieldCheck, User, Trash2, Loader2, Lock } from 'lucide-react';
import { api } from '../../api/client';
import { showToast } from '../../components/common/Toast';
import Modal from '../../components/common/Modal';
import PasswordInput from '../../components/common/PasswordInput';
import { formatUzPhone } from '../../utils/helpers';

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    fullName: '',
    username: '',
    phone: '',
    password: '',
    role: 'ADMIN',
  });

  const loadUsers = (silent = false) => {
    if (!silent) setLoading(true);
    api.get('/auth/users')
      .then((res) => {
        if (res?.success) setUsers(res.users || res.staff || []);
      })
      .catch((err) => showToast(err.message, 'error'))
      .finally(() => {
        if (!silent) setLoading(false);
      });
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleOpenAdd = () => {
    setFormData({
      fullName: '',
      username: '',
      phone: '+998 ()',
      password: '',
      role: 'ADMIN',
    });
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.password) {
      showToast('To‘liq ism va parolni kiriting', 'error');
      return;
    }

    setSaving(true);
    try {
      const cleanUsername = (formData.username || formData.fullName.toLowerCase().replace(/[^a-z0-9]/g, '')).trim().toLowerCase().replace(/^@+/, '');
      const payload = {
        fullName: formData.fullName.trim(),
        username: cleanUsername,
        password: formData.password.trim(),
        role: formData.role,
        phone: formData.phone && formData.phone !== '+998 ()' ? formData.phone.trim() : null,
        email: `${cleanUsername}@system.local`,
      };

      const res = await api.post('/auth/users', payload);
      if (res?.success) {
        showToast(`Yangi foydalanuvchi yaratildi! Login: @${cleanUsername}`, 'success');
        setIsModalOpen(false);
        loadUsers(true);
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (targetUser) => {
    if (targetUser.role === 'SUPER_ADMIN' || targetUser.username === 'admin') {
      showToast('Bosh administratorni o‘chirish qat’iyan man etiladi!', 'warning');
      return;
    }

    const confirmed = window.confirm(
      `Haqiqatan ham "${targetUser.fullName}" (@${targetUser.username}) hisobini butunlay o‘chirib tashlamoqchimisiz? Ushbu akkaunt tizimdan to‘liq o‘chiriladi.`
    );
    if (!confirmed) return;

    setDeletingId(targetUser.id);
    try {
      const res = await api.delete(`/auth/users/${targetUser.id}`);
      if (res?.success) {
        showToast('Foydalanuvchi muvaffaqiyatli o‘chirildi', 'success');
        setUsers((prev) => prev.filter((u) => u.id !== targetUser.id));
      } else {
        showToast(res?.message || 'O‘chirishda xatolik yuz berdi', 'error');
      }
    } catch (err) {
      showToast(err.message || 'O‘chirishda xatolik yuz berdi', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (targetUser) => {
    if (targetUser.role === 'SUPER_ADMIN' || targetUser.username === 'admin') {
      showToast('Bosh administratorni bloklash taqiqlangan! Asosiy admin doim faol bo‘lishi shart.', 'warning');
      return;
    }

    const nextState = !targetUser.isActive;
    setTogglingId(targetUser.id);

    // Optimistic update: instantly update UI with zero layout shift or table jumping!
    setUsers((prev) =>
      prev.map((u) => (u.id === targetUser.id ? { ...u, isActive: nextState } : u))
    );

    try {
      const res = await api.put(`/auth/users/${targetUser.id}`, { isActive: nextState });
      if (res?.success) {
        showToast(
          nextState ? 'Foydalanuvchi muvaffaqiyatli faollashtirildi' : 'Foydalanuvchi muvaffaqiyatli bloklandi',
          'success'
        );
      } else {
        // Revert on error
        setUsers((prev) =>
          prev.map((u) => (u.id === targetUser.id ? { ...u, isActive: !nextState } : u))
        );
        showToast(res?.message || 'Xatolik yuz berdi', 'error');
      }
    } catch (err) {
      // Revert on error
      setUsers((prev) =>
        prev.map((u) => (u.id === targetUser.id ? { ...u, isActive: !nextState } : u))
      );
      showToast(err.message, 'error');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-lg font-bold text-slate-900">
            Admin Foydalanuvchilar & Xodimlar ({users.length})
          </h2>
          <p className="text-xs text-slate-500">
            Tizimga kirish huquqiga ega bo‘lgan ma’murlar va ularning rollari
          </p>
        </div>

        <button
          onClick={handleOpenAdd}
          className="inline-flex items-center px-4 py-2 text-xs font-semibold text-[#091322] bg-[#c5a880] hover:bg-[#d4b993] rounded shadow-sm transition-colors"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          Yangi foydalanuvchi
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4 whitespace-nowrap">Foydalanuvchi</th>
                <th className="py-3 px-4 whitespace-nowrap">Login (Username)</th>
                <th className="py-3 px-4 whitespace-nowrap">Telefon</th>
                <th className="py-3 px-4 whitespace-nowrap">Rol</th>
                <th className="py-3 px-4 whitespace-nowrap">Holat</th>
                <th className="py-3 px-4 whitespace-nowrap">Yaratilgan</th>
                <th className="py-3 px-4 text-right whitespace-nowrap min-w-[200px]">Amallar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-400">Yuklanmoqda...</td>
                </tr>
              ) : users.map((u) => (
                <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                  <td className="py-3 px-4 font-semibold text-slate-900 whitespace-nowrap">
                    {u.fullName}
                  </td>
                  <td className="py-3 px-4 font-mono text-slate-700 font-medium whitespace-nowrap">
                    @{u.username || '—'}
                  </td>
                  <td className="py-3 px-4 font-mono text-slate-600 whitespace-nowrap tabular-nums">
                    {u.phone ? formatUzPhone(u.phone) : '—'}
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-[#091322] text-[#c5a880]">
                      <ShieldCheck className="w-3 h-3 mr-1" />
                      {u.role}
                    </span>
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      u.isActive ? 'bg-emerald-50 text-emerald-800' : 'bg-slate-100 text-slate-600 border border-slate-200'
                    }`}>
                      {u.isActive ? 'Faol' : 'Bloklangan'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-slate-400 whitespace-nowrap font-mono">
                    {new Date(u.createdAt).toLocaleDateString()}
                  </td>
                  <td className="py-3 px-4 text-right whitespace-nowrap">
                    {u.role === 'SUPER_ADMIN' || u.username === 'admin' ? (
                      <span className="inline-flex items-center px-2.5 py-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded select-none shadow-2xs">
                        <ShieldCheck className="w-3.5 h-3.5 mr-1 text-emerald-600" />
                        Bosh Admin (Himoyalangan)
                      </span>
                    ) : (
                      <div className="inline-flex items-center space-x-1.5 justify-end">
                        <button
                          type="button"
                          onClick={() => handleToggleActive(u)}
                          disabled={togglingId === u.id}
                          className={`px-2.5 py-1 text-[11px] font-medium rounded border transition-colors cursor-pointer inline-flex items-center shadow-2xs ${
                            u.isActive
                              ? 'text-slate-700 bg-slate-50 hover:bg-slate-100 border-slate-300'
                              : 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border-emerald-200'
                          } ${togglingId === u.id ? 'opacity-60 cursor-wait' : ''}`}
                        >
                          {togglingId === u.id && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                          {u.isActive ? 'Bloklash' : 'Faollashtirish'}
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDeleteUser(u)}
                          disabled={deletingId === u.id}
                          className="px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:text-slate-900 bg-slate-50 hover:bg-slate-200/90 border border-slate-300 rounded transition-colors cursor-pointer inline-flex items-center shadow-2xs disabled:opacity-50"
                          title="Foydalanuvchi hisobini butunlay o‘chirish"
                        >
                          {deletingId === u.id ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin text-slate-500" />
                          ) : (
                            <Trash2 className="w-3.5 h-3.5 mr-1 text-slate-500" />
                          )}
                          O‘chirish
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Yangi Admin Foydalanuvchi Yaratish"
        maxWidth="max-w-md"
      >
        <form onSubmit={handleSave} className="space-y-4 text-xs">
          <div>
            <label className="block font-semibold text-slate-700 uppercase mb-1">To‘liq Ism *</label>
            <input
              type="text"
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              placeholder="Masalan: Abdulazizxon Asrorov"
              className="w-full px-3 py-2 border rounded focus:border-[#c5a880]"
              required
            />
          </div>

          <div>
            <label className="block font-semibold text-slate-700 uppercase mb-1">Login (Username) *</label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().trim() })}
              placeholder="Masalan: abdulazizxon"
              className="w-full px-3 py-2 border rounded font-mono focus:border-[#c5a880]"
              required
            />
          </div>

          <div>
            <label className="block font-semibold text-slate-700 uppercase mb-1">Telefon raqami</label>
            <input
              type="tel"
              value={formData.phone || '+998 ()'}
              onFocus={() => {
                if (!formData.phone) setFormData((p) => ({ ...p, phone: '+998 ()' }));
              }}
              onChange={(e) => setFormData({ ...formData, phone: formatUzPhone(e.target.value, formData.phone) })}
              placeholder="+998 (97) 325-07-70"
              className="w-full px-3 py-2 border rounded font-mono focus:border-[#c5a880]"
            />
          </div>

          <div>
            <label className="block font-semibold text-slate-700 uppercase mb-1">Parol (Kamida 6 ta belgi) *</label>
            <PasswordInput
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              minLength={6}
              placeholder="Kamida 6 ta belgi..."
              className="w-full px-3 py-2 border rounded font-mono focus:border-[#c5a880]"
              required
            />
          </div>

          <div>
            <label className="block font-semibold text-slate-700 uppercase mb-1">Rol *</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="w-full px-3 py-2 border rounded focus:border-[#c5a880]"
            >
              <option value="ADMIN">Admin (Boshqaruv & Kontent)</option>
              <option value="LAWYER">Advokat (O‘z qabullarini ko‘rish)</option>
              <option value="CLIENT">Foydalanuvchi / Mijoz (Saytdan to‘liq bepul foydalanish)</option>
              <option value="RECEPTIONIST">Kotibiyat / Operator</option>
            </select>
          </div>

          <div className="pt-4 border-t flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 border rounded text-slate-700 hover:bg-slate-50 cursor-pointer"
            >
              Bekor qilish
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-[#091322] text-[#c5a880] font-semibold rounded hover:bg-[#15243d] cursor-pointer disabled:opacity-50"
            >
              {saving ? 'Yaratilmoqda...' : 'Yaratish'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
